import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getMemberId } from '@nx-frontend/shared/store/org-members';
import { ManageMembersType, ManageMembersView, Permissions } from '@nx-frontend/shared/types';
import { getNodeNameByType, getPermission } from '@nx-frontend/shared/utils';
import {
  $menuItem,
  $portalClientAccess,
  $sortedByRoleMembers,
  $manageMembersWorkspaceMemberUserIds,
  updateMenuItemEvent,
  updatePermissionsFx,
  updateViewModeEvent,
  $portalConsoleAccess,
  $manageMembersType,
  $currentUserCanInvite,
} from '../store';
import { getPortalClientOrgRolesFromAccess, getPortalConsoleOrgRolesFromAccess, getUpdatedPermissions } from '../utils';
import NodeAccessItem from './node-access-item';

export function NodeAccessList({
  onUpdateMenuItemPermissions,
}: {
  onUpdateMenuItemPermissions?: (id: string, permissions: Permissions) => void;
}) {
  const [updateViewMode, updateMenuItem, updatePermissions] = useEvent([
    updateViewModeEvent,
    updateMenuItemEvent,
    updatePermissionsFx,
  ]);
  const [
    menuItem,
    portalClientAccess,
    portalConsoleAccess,
    members,
    workspaceMemberUserIds,
    manageMembersType,
    currentUserCanInvite,
  ] = useUnit([
    $menuItem,
    $portalClientAccess,
    $portalConsoleAccess,
    $sortedByRoleMembers,
    $manageMembersWorkspaceMemberUserIds,
    $manageMembersType,
    $currentUserCanInvite,
  ]);
  const t = useTranslations('features');
  const itemPermissions = menuItem?.permissions;
  const availablePortalOrgRoles = getPortalClientOrgRolesFromAccess(portalClientAccess);
  const availableConsoleOrgRoles = getPortalConsoleOrgRolesFromAccess(portalConsoleAccess);
  const { toast } = useToast();

  const nodeName = menuItem ? getNodeNameByType(menuItem.type) : '';

  const memberIds = useMemo(() => {
    if (itemPermissions) {
      return members.reduce((acc, member) => {
        if (
          workspaceMemberUserIds.includes(member.userId) &&
          availablePortalOrgRoles.includes(member.role) &&
          (getPermission({ userId: member.userId, permissions: itemPermissions }) ||
            availableConsoleOrgRoles.includes(member.role))
        ) {
          acc.push(getMemberId(member));
        }
        return acc;
      }, [] as string[]);
    }
    return [];
  }, [availableConsoleOrgRoles, availablePortalOrgRoles, itemPermissions, members, workspaceMemberUserIds]);

  const handleRemove = async (userId: number) => {
    if (itemPermissions) {
      const availableUserIds = members
        .filter(
          (member) =>
            workspaceMemberUserIds.includes(member.userId) &&
            (availablePortalOrgRoles.includes(member.role) || availableConsoleOrgRoles.includes(member.role))
        )
        .map((member) => member.userId);
      const permissions = getUpdatedPermissions(itemPermissions, availableUserIds, userId, false);
      const initialPermission = { ...itemPermissions };

      updateMenuItem({ ...menuItem, permissions });
      if (onUpdateMenuItemPermissions) {
        onUpdateMenuItemPermissions(menuItem.id, permissions);
      }
      updatePermissions({
        permissions,
        targetId: menuItem.id,
      })
        .then(() => {
          const nodeName = menuItem ? getNodeNameByType(menuItem.type) : '';
          toast({
            title: t('manage-members.denied_access_toast', { nodeName: nodeName }),
          });
        })
        .catch(() => {
          updateMenuItem({ ...menuItem, permissions: initialPermission });
          if (onUpdateMenuItemPermissions) {
            onUpdateMenuItemPermissions(menuItem.id, initialPermission);
          }
          toast({
            title: t('manage-members.an_error_changing_permissions'),
          });
        });
    }
  };

  const handleInviteClick = () => {
    if (manageMembersType === ManageMembersType.PORTAL_NODE) {
      if (currentUserCanInvite) {
        updateViewMode(ManageMembersView.INVITE);
      } else {
        updateViewMode(ManageMembersView.ORG_ACCESS_LIST);
      }
    }
  };

  return (
    <div className="w-full flex flex-col max-h-full items-start -mt-7">
      <div className="w-full text-xs font-semibold pb-2 border-b border-neutral-200">
        {t('manage-members.user_with_access', { itemType: nodeName })}
      </div>
      <div className="overflow-hidden overflow-y-auto min-h-0 h-full w-full pt-2 mt-2">
        {memberIds.map((memberId, index) => (
          <NodeAccessItem
            key={memberId}
            memberId={memberId}
            isLast={index === memberIds.length - 1}
            onRemove={handleRemove}
          />
        ))}
      </div>

      <Button type={ButtonType.BASIC} className="mt-2 !px-2" onClick={handleInviteClick}>
        <Icon icon={IconsMap.PLUS} className="w-5 h-5" />
        <span className="ml-2">{t('manage-members.invite_more')}</span>
      </Button>
    </div>
  );
}

export default NodeAccessList;
